import {
	Button,
	cn,
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Separator,
} from "@monbits/ui";
import { X } from "lucide-react";
import * as React from "react";

interface DrawerFormProps {
	title: string;
	description?: string;
	isOpen: boolean;
	onClose: () => void;
	onSubmit?: () => void;
	isSubmitting?: boolean;
	submitText?: string;
	width?: number;
	children?: React.ReactNode;
}

const DrawerForm: React.FC<DrawerFormProps> = ({
	title,
	description,
	isOpen,
	onClose,
	onSubmit,
	isSubmitting,
	submitText,
	width,
	children,
}) => {
	const contentRef = React.useRef<HTMLDivElement>(null);

	// Footer button submits the form rendered in our content area, scoped via
	// ref so we never accidentally dispatch to a form mounted elsewhere on
	// the page. Falls back to onSubmit when there's no form (non-form drawers).
	const handleFooterSubmit = () => {
		const form = contentRef.current?.querySelector("form");
		if (form) {
			form.requestSubmit();
			return;
		}
		onSubmit?.();
	};

	return (
		<Drawer open={isOpen} onOpenChange={onClose} direction="right">
			<DrawerContent className="h-screen overflow-hidden flex flex-col">
				<div
					className={cn(
						"flex flex-col h-full w-full",
						"sm:w-96 sm:max-w-sm",
						"md:w-[512px] md:max-w-lg",
					)}
					style={
						width
							? {
									width: `min(100vw, ${width}px)`,
								}
							: undefined
					}
				>
					<DrawerHeader className="flex-shrink-0 relative">
						<DrawerTitle className="text-lg font-medium pr-8">
							{title}
						</DrawerTitle>
						{description ? (
							<DrawerDescription className="text-sm text-muted-foreground">
								{description}
							</DrawerDescription>
						) : (
							<span className="sr-only">{title}</span>
						)}
						<DrawerClose asChild>
							<Button
								variant="ghost"
								size="icon"
								className="absolute right-4 top-4 h-8 w-8 rounded-full"
							>
								<X className="h-4 w-4" />
								<span className="sr-only">Close</span>
							</Button>
						</DrawerClose>
					</DrawerHeader>

					<Separator />

					<div className="flex-1 overflow-y-auto">
						<div ref={contentRef} className="p-6 space-y-6">
							{children}
						</div>
					</div>

					<Separator />
					<DrawerFooter className="flex-shrink-0">
						<div className="flex gap-3">
							{(onSubmit || submitText) && (
								<Button
									onClick={handleFooterSubmit}
									disabled={isSubmitting}
									className="flex-1"
									size="lg"
								>
									{isSubmitting ? "Submitting..." : submitText || "Submit"}
								</Button>
							)}
							<DrawerClose asChild>
								<Button
									variant="outline"
									className={cn(
										"flex-1",
										onSubmit || submitText ? "" : "w-full",
									)}
									size="lg"
									disabled={isSubmitting}
								>
									{onSubmit || submitText ? "Cancel" : "Close"}
								</Button>
							</DrawerClose>
						</div>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};

export default DrawerForm;
